import { useState } from 'react';
import { useToast } from '../../lib/use-toast';
import { attachTrailerDocument, getTrailerDocumentDownloadUrl, uploadTrailerDocumentFile } from './trailers.api';

export interface DocumentFormInput {
  documentTypeId: string;
  number: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  file: File;
}

/** The Documents tab's mutations: presign → direct upload → confirm (CLAUDE.md → storage rule) — the file never passes through the API body. Add and renew are the same call; the server decides which it was. */
export function useTrailerDocuments(trailerId: string, reload: () => Promise<void>) {
  const [documentSubmitting, setDocumentSubmitting] = useState(false);
  const toast = useToast();

  async function addDocument(input: DocumentFormInput, isRenewal: boolean): Promise<boolean> {
    setDocumentSubmitting(true);
    try {
      const { key, contentType } = await uploadTrailerDocumentFile(trailerId, input.file);
      await attachTrailerDocument(trailerId, {
        key,
        contentType,
        documentTypeId: input.documentTypeId,
        number: input.number,
        issuer: input.issuer,
        issueDate: input.issueDate,
        expiryDate: input.expiryDate,
      });
      await reload();
      toast.success(isRenewal ? 'Document renewed' : 'Document added');
      return true;
    } catch {
      toast.error(isRenewal ? "Couldn't renew the document" : "Couldn't add the document");
      return false;
    } finally {
      setDocumentSubmitting(false);
    }
  }

  async function openDocument(documentId: string): Promise<void> {
    try {
      const { url } = await getTrailerDocumentDownloadUrl(trailerId, documentId);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error("Couldn't open the document");
    }
  }

  return { documentSubmitting, addDocument, openDocument };
}
